import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import { supabase } from '@/lib/supabase';
import { queryKeys } from '@/lib/query-client';
import type { InventoryReceipt, MeasurementUnit } from '@/types/entities';
import { toast } from 'sonner';
import { isAssistantLoggedIn } from '@/lib/permissions';

// Time filter options
export type TimeFilter = 'today' | 'week' | 'month' | 'all';

// Filters for querying receipts
export interface ReceiptFilters {
  ingredientId?: string | undefined;
  startDate?: Date | undefined;
  endDate?: Date | undefined;
  timeFilter?: TimeFilter | undefined;
  createdBy?: string | undefined;
  page?: number | undefined;
  pageSize?: number | undefined;
}

// Input for adding a receipt
export interface AddReceiptInput {
  ingredientId: string;
  quantityReceived: number;
  unit: MeasurementUnit;
  costPerUnit: number;
  receiptDate?: Date;
  supplier?: string | null | undefined;
  invoiceNumber?: string | null | undefined;
  notes?: string | null | undefined;
  minThreshold?: number | undefined; // Optional: only used when creating new inventory
  batchId?: string | null | undefined; // Optional: receipts with same batchId are grouped together
}

// Helper to compute date range from time filter
function getDateRangeFromTimeFilter(timeFilter: TimeFilter): { startDate?: Date; endDate?: Date } {
  const now = new Date();
  now.setHours(23, 59, 59, 999);

  switch (timeFilter) {
    case 'today': {
      const startDate = new Date(now);
      startDate.setHours(0, 0, 0, 0);
      return { startDate, endDate: now };
    }
    case 'week': {
      const startDate = new Date(now);
      startDate.setDate(now.getDate() - 7);
      startDate.setHours(0, 0, 0, 0);
      return { startDate, endDate: now };
    }
    case 'month': {
      const startDate = new Date(now);
      startDate.setMonth(now.getMonth() - 1);
      startDate.setHours(0, 0, 0, 0);
      return { startDate, endDate: now };
    }
    case 'all':
    default:
      return {};
  }
}

// Response interface with pagination metadata
export interface ReceiptsResponse {
  data: InventoryReceipt[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

/**
 * Fetch inventory receipts for a restaurant with optional filters
 */
export function useInventoryReceipts(
  restaurantId: string,
  filters?: ReceiptFilters
) {
  const page = filters?.page ?? 1;
  const pageSize = filters?.pageSize ?? 10;

  // Compute date range from time filter or use explicit dates
  const dateRange = filters?.timeFilter
    ? getDateRangeFromTimeFilter(filters.timeFilter)
    : { startDate: filters?.startDate, endDate: filters?.endDate };

  // Serialize filters for stable query key
  const queryKey = useMemo(
    () => [
      'receipts',
      restaurantId,
      filters?.ingredientId,
      filters?.timeFilter,
      filters?.createdBy,
      dateRange.startDate?.toISOString(),
      dateRange.endDate?.toISOString(),
      page,
      pageSize,
    ],
    [restaurantId, filters?.ingredientId, filters?.timeFilter, filters?.createdBy, dateRange.startDate?.getTime(), dateRange.endDate?.getTime(), page, pageSize]
  );

  return useQuery({
    queryKey,
    queryFn: async (): Promise<ReceiptsResponse> => {
      // Check if assistant is logged in
      if (isAssistantLoggedIn()) {
        // Use assistant RPC function
        const { data, error } = await supabase.rpc('assistant_get_inventory_receipts', {
          p_restaurant_id: restaurantId,
        });

        if (error) {
          if (error.message?.includes('PERMISSION_DENIED')) {
            console.warn('Assistant does not have permission to view inventory receipts');
            return { data: [], totalCount: 0, page, pageSize, totalPages: 0 };
          }
          throw error;
        }

        // Fetch ingredients for the receipts using RPC (assistants can't query tables directly)
        const { data: ingredients, error: ingredientsError } = await supabase.rpc('assistant_get_ingredients', {
          p_restaurant_id: restaurantId,
        });
        if (ingredientsError) {
          console.warn('Failed to fetch ingredients for assistant:', ingredientsError);
        }
        const ingredientsMap = new Map((ingredients || []).map((i: any) => [i.id, i]));

        // Map to InventoryReceipt and apply client-side filtering
        let receipts = (data || []).map((r: any): InventoryReceipt & { createdByDisplay?: string; updatedByDisplay?: string; batchId?: string } => {
          const ingredient = ingredientsMap.get(r.ingredient_id);
          const receipt: InventoryReceipt & { createdByDisplay?: string; updatedByDisplay?: string; batchId?: string } = {
            id: r.id,
            restaurantId: r.restaurant_id,
            ingredientId: r.ingredient_id,
            inventoryId: r.inventory_id,
            receiptDate: r.receipt_date,
            quantityReceived: r.quantity_received || r.quantity,
            unit: r.unit as MeasurementUnit,
            costPerUnit: r.cost_per_unit,
            totalCost: r.total_cost ?? 0,
            supplier: r.supplier,
            invoiceNumber: r.invoice_number,
            notes: r.notes,
            createdAt: r.created_at || '',
            createdBy: r.created_by,
            deletedAt: r.deleted_at || null,
            updatedAt: r.updated_at || null,
          };
          // Batch ID for grouping
          if (r.batch_id) receipt.batchId = r.batch_id;
          // Audit display fields from RPC
          if (r.created_by_display) receipt.createdByDisplay = r.created_by_display;
          if (r.updated_by_display) receipt.updatedByDisplay = r.updated_by_display;
          if (ingredient) {
            receipt.ingredient = {
              id: ingredient.id,
              restaurantId: ingredient.restaurant_id,
              nameAr: ingredient.name_ar,
              nameFr: ingredient.name_fr,
              emoji: ingredient.emoji || '',
              createdAt: ingredient.created_at || '',
              updatedAt: ingredient.updated_at || '',
            };
          }
          return receipt;
        });

        // Apply client-side filtering
        if (filters?.ingredientId) {
          receipts = receipts.filter(r => r.ingredientId === filters.ingredientId);
        }
        if (dateRange.startDate) {
          receipts = receipts.filter(r => new Date(r.receiptDate) >= dateRange.startDate!);
        }
        if (dateRange.endDate) {
          receipts = receipts.filter(r => new Date(r.receiptDate) <= dateRange.endDate!);
        }
        if (filters?.createdBy) {
          receipts = receipts.filter(r => (r as any).createdByDisplay === filters.createdBy);
        }

        // Sort by date descending
        receipts.sort((a, b) => new Date(b.receiptDate).getTime() - new Date(a.receiptDate).getTime());

        // Client-side pagination
        const totalCount = receipts.length;
        const totalPages = Math.ceil(totalCount / pageSize);
        const offset = (page - 1) * pageSize;
        const paginatedReceipts = receipts.slice(offset, offset + pageSize);

        return { data: paginatedReceipts, totalCount, page, pageSize, totalPages };
      }

      // Admin user - use direct query (fetch all for time range, filter + paginate client-side)
      let query = supabase
        .from('inventory_receipts')
        .select(`
          *,
          ingredient:ingredients(*),
          created_by_assistant:assistants!inventory_receipts_created_by_assistant_id_fkey(username),
          updated_by_assistant:assistants!inventory_receipts_updated_by_assistant_id_fkey(username)
        `)
        .eq('restaurant_id', restaurantId)
        .order('receipt_date', { ascending: false });

      // Apply filters
      if (filters?.ingredientId) {
        query = query.eq('ingredient_id', filters.ingredientId);
      }
      if (dateRange.startDate) {
        query = query.gte('receipt_date', dateRange.startDate.toISOString());
      }
      if (dateRange.endDate) {
        query = query.lte('receipt_date', dateRange.endDate.toISOString());
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error fetching receipts:', error);
        throw error;
      }

      // Map snake_case database fields to camelCase TypeScript interface
      let receipts = (data || []).map((r: any): InventoryReceipt & { createdByDisplay?: string; updatedByDisplay?: string; batchId?: string } => {
        const receipt: InventoryReceipt & { createdByDisplay?: string; updatedByDisplay?: string; batchId?: string } = {
          id: r.id,
          restaurantId: r.restaurant_id,
          ingredientId: r.ingredient_id,
          inventoryId: r.inventory_id,
          receiptDate: r.receipt_date,
          quantityReceived: r.quantity_received,
          unit: r.unit as MeasurementUnit,
          costPerUnit: r.cost_per_unit,
          totalCost: r.total_cost ?? 0,
          supplier: r.supplier,
          invoiceNumber: r.invoice_number,
          notes: r.notes,
          createdAt: r.created_at || '',
          createdBy: r.created_by,
          deletedAt: r.deleted_at || null,
          updatedAt: r.updated_at || null,
        };
        // Batch ID for grouping
        if (r.batch_id) receipt.batchId = r.batch_id;
        // For admin: show assistant username or "Admin"
        if (r.created_by_assistant_id && r.created_by_assistant?.username) {
          receipt.createdByDisplay = r.created_by_assistant.username;
        } else if (r.created_by) {
          receipt.createdByDisplay = 'Admin';
        }
        // For updates/deletions: show assistant username or "Admin"
        if (r.updated_by_assistant_id && r.updated_by_assistant?.username) {
          receipt.updatedByDisplay = r.updated_by_assistant.username;
        } else if (r.deleted_at) {
          receipt.updatedByDisplay = 'Admin';
        }
        if (r.ingredient) {
          receipt.ingredient = {
            id: r.ingredient.id,
            restaurantId: r.ingredient.restaurant_id,
            nameAr: r.ingredient.name_ar,
            nameFr: r.ingredient.name_fr,
            emoji: r.ingredient.emoji || '',
            createdAt: r.ingredient.created_at || '',
            updatedAt: r.ingredient.updated_at || '',
          };
        }
        return receipt;
      });

      // Filter by createdBy
      if (filters?.createdBy) {
        receipts = receipts.filter(r => (r as any).createdByDisplay === filters.createdBy);
      }

      // Client-side pagination
      const totalCount = receipts.length;
      const totalPages = Math.ceil(totalCount / pageSize);
      const offset = (page - 1) * pageSize;
      const paginatedReceipts = receipts.slice(offset, offset + pageSize);

      return {
        data: paginatedReceipts,
        totalCount,
        page,
        pageSize,
        totalPages,
      };
    },
    enabled: !!restaurantId,
  });
}

/**
 * Add a new inventory receipt
 * Supports both admin users and assistants with appropriate permissions
 */
export function useAddReceipt(restaurantId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (receipt: AddReceiptInput) => {
      // Check if assistant is logged in
      if (isAssistantLoggedIn()) {

        // Use assistant RPC function
        const rpcParams: Record<string, any> = {
          
          p_restaurant_id: restaurantId,
          p_ingredient_id: receipt.ingredientId,
          p_quantity: receipt.quantityReceived,
          p_unit: receipt.unit,
          p_cost_per_unit: receipt.costPerUnit,
          p_receipt_date: receipt.receiptDate?.toISOString() || new Date().toISOString(),
        };
        if (receipt.supplier) rpcParams['p_supplier'] = receipt.supplier;
        if (receipt.notes) rpcParams['p_notes'] = receipt.notes;
        if (receipt.minThreshold !== undefined) rpcParams['p_min_threshold'] = receipt.minThreshold;
        if (receipt.batchId) rpcParams['p_batch_id'] = receipt.batchId;

        const { data, error } = await supabase.rpc('assistant_create_inventory_receipt', rpcParams as any);

        if (error) {
          if (error.message?.includes('PERMISSION_DENIED')) {
            throw new Error('Permission denied');
          }
          console.error('Error adding receipt:', error);
          throw error;
        }

        return data as string;
      }

      // Admin user - use direct RPC
      // Build params object with only defined values for optional fields
      const params: {
        p_restaurant_id: string;
        p_ingredient_id: string;
        p_quantity_received: number;
        p_unit: MeasurementUnit;
        p_cost_per_unit: number;
        p_receipt_date?: string;
        p_supplier?: string;
        p_invoice_number?: string;
        p_notes?: string;
        p_min_threshold?: number;
        p_batch_id?: string;
      } = {
        p_restaurant_id: restaurantId,
        p_ingredient_id: receipt.ingredientId,
        p_quantity_received: receipt.quantityReceived,
        p_unit: receipt.unit,
        p_cost_per_unit: receipt.costPerUnit,
      };

      if (receipt.receiptDate) params.p_receipt_date = receipt.receiptDate.toISOString();
      if (receipt.supplier) params.p_supplier = receipt.supplier;
      if (receipt.invoiceNumber) params.p_invoice_number = receipt.invoiceNumber;
      if (receipt.notes) params.p_notes = receipt.notes;
      if (receipt.minThreshold !== undefined) params.p_min_threshold = receipt.minThreshold;
      if (receipt.batchId) params.p_batch_id = receipt.batchId;

      const { data, error } = await supabase.rpc('add_inventory_receipt', params);

      if (error) {
        console.error('Error adding receipt:', error);
        throw error;
      }

      return data as string; // Returns receipt_id
    },
    onSuccess: () => {
      // Invalidate all related queries using correct query keys
      queryClient.invalidateQueries({ queryKey: ['receipts', restaurantId] });
      // Use queryKeys for inventory to match useInventory hook
      queryClient.invalidateQueries({ queryKey: queryKeys.inventory.all() });
      queryClient.invalidateQueries({ queryKey: queryKeys.inventory.list({ restaurantId }) });
      queryClient.invalidateQueries({ queryKey: ['cost-analytics', restaurantId] });
      queryClient.invalidateQueries({ queryKey: ['inventory-insights'] });
      // Invalidate menu item availability since inventory stock changed
      queryClient.invalidateQueries({ queryKey: ['menuItemAvailability', restaurantId] });
      queryClient.invalidateQueries({ queryKey: ['menuItemOptionAvailability'] });

      toast.success('Receipt added successfully');
    },
    onError: (error: Error) => {
      toast.error('Failed to add receipt: ' + error.message);
    },
  });
}

// Input for adding multiple receipts with shared supplier/invoice
export interface AddMultiReceiptInput {
  receiptDate: Date;
  supplier: string;
  invoiceNumber?: string;
  notes?: string;
  items: Array<{
    ingredientId: string;
    quantityReceived: number;
    unit: MeasurementUnit;
    costPerUnit: number;
  }>;
}

/**
 * Add multiple inventory receipts with shared supplier and invoice number
 * Supports both admin users and assistants with appropriate permissions
 */
export function useAddMultiReceipt(restaurantId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: AddMultiReceiptInput) => {
      const receiptIds: string[] = [];
      const errors: Array<{ ingredientId: string; error: string }> = [];

      // Check if assistant is logged in
      const isAssistant = isAssistantLoggedIn();

      // Generate a batch ID only if adding multiple items (for grouping)
      const batchId = input.items.length > 1 ? crypto.randomUUID() : undefined;

      // Process each receipt sequentially
      for (const item of input.items) {
        try {
          if (isAssistant) {
            // Use assistant RPC function
            const rpcParams: Record<string, any> = {

              p_restaurant_id: restaurantId,
              p_ingredient_id: item.ingredientId,
              p_quantity: item.quantityReceived,
              p_unit: item.unit,
              p_cost_per_unit: item.costPerUnit,
              p_receipt_date: input.receiptDate.toISOString(),
              p_supplier: input.supplier,
            };
            if (input.notes) rpcParams['p_notes'] = input.notes;
            if (batchId) rpcParams['p_batch_id'] = batchId;
            const { data, error } = await supabase.rpc('assistant_create_inventory_receipt', rpcParams as any);

            if (error) {
              if (error.message?.includes('PERMISSION_DENIED')) {
                throw new Error('Permission denied');
              }
              console.error(`Error adding receipt for ingredient ${item.ingredientId}:`, error);
              errors.push({
                ingredientId: item.ingredientId,
                error: error.message,
              });
            } else {
              receiptIds.push(data as string);
            }
          } else {
            // Admin user - use direct RPC
            const params: {
              p_restaurant_id: string;
              p_ingredient_id: string;
              p_quantity_received: number;
              p_unit: MeasurementUnit;
              p_cost_per_unit: number;
              p_receipt_date: string;
              p_supplier: string;
              p_invoice_number?: string;
              p_notes?: string;
              p_batch_id?: string;
            } = {
              p_restaurant_id: restaurantId,
              p_ingredient_id: item.ingredientId,
              p_quantity_received: item.quantityReceived,
              p_unit: item.unit,
              p_cost_per_unit: item.costPerUnit,
              p_receipt_date: input.receiptDate.toISOString(),
              p_supplier: input.supplier,
            };

            if (input.invoiceNumber) {
              params.p_invoice_number = input.invoiceNumber;
            }
            if (input.notes) {
              params.p_notes = input.notes;
            }
            if (batchId) {
              params.p_batch_id = batchId;
            }

            const { data, error } = await supabase.rpc('add_inventory_receipt', params);

            if (error) {
              console.error(`Error adding receipt for ingredient ${item.ingredientId}:`, error);
              errors.push({
                ingredientId: item.ingredientId,
                error: error.message,
              });
            } else {
              receiptIds.push(data as string);
            }
          }
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Unknown error';
          console.error(`Error adding receipt for ingredient ${item.ingredientId}:`, error);
          errors.push({
            ingredientId: item.ingredientId,
            error: errorMessage,
          });
        }
      }

      // If all failed, throw error
      if (receiptIds.length === 0) {
        throw new Error(
          `Failed to add all receipts: ${errors.map((e) => e.error).join('; ')}`
        );
      }

      // If some failed, show warning but don't throw
      if (errors.length > 0) {
        console.warn('Some receipts failed to add:', errors);
        toast.warning(
          `Added ${receiptIds.length} of ${input.items.length} receipts. Some failed.`
        );
      }

      return {
        receiptIds,
        errors,
        successCount: receiptIds.length,
        totalCount: input.items.length,
      };
    },
    onSuccess: () => {
      // Invalidate all related queries
      queryClient.invalidateQueries({ queryKey: ['receipts', restaurantId] });
      queryClient.invalidateQueries({ queryKey: queryKeys.inventory.all() });
      queryClient.invalidateQueries({ queryKey: queryKeys.inventory.list({ restaurantId }) });
      queryClient.invalidateQueries({ queryKey: ['cost-analytics', restaurantId] });
      queryClient.invalidateQueries({ queryKey: ['inventory-insights'] });
      // Invalidate menu item availability since inventory stock changed
      queryClient.invalidateQueries({ queryKey: ['menuItemAvailability', restaurantId] });
      queryClient.invalidateQueries({ queryKey: ['menuItemOptionAvailability'] });

      toast.success('Receipts added successfully');
    },
    onError: (error: Error) => {
      toast.error('Failed to add receipts: ' + error.message);
    },
  });
}

// Input for updating a receipt
export interface UpdateReceiptInput {
  receiptId: string;
  quantityReceived: number;
  unit: string;
  costPerUnit: number;
  receiptDate: Date;
  supplier?: string | null;
  invoiceNumber?: string | null;
  notes?: string | null;
}

/**
 * Hook to update an existing receipt with inventory recalculation
 */
export function useUpdateReceipt(restaurantId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: UpdateReceiptInput) => {
      if (isAssistantLoggedIn()) {

        const { data, error } = await supabase.rpc('assistant_update_inventory_receipt' as any, {
          p_restaurant_id: restaurantId,
          p_receipt_id: input.receiptId,
          p_quantity: input.quantityReceived,
          p_unit: input.unit,
          p_cost_per_unit: input.costPerUnit,
          p_receipt_date: input.receiptDate.toISOString().split('T')[0], // DATE format
          p_supplier: input.supplier || null,
          p_notes: input.notes || null,
        });

        if (error) {
          if (error.message?.includes('PERMISSION_DENIED')) {
            throw new Error('Permission denied: You do not have permission to edit receipts');
          }
          throw error;
        }

        return data;
      }

      // Admin user
      const { data, error } = await supabase.rpc('update_inventory_receipt' as any, {
        p_receipt_id: input.receiptId,
        p_restaurant_id: restaurantId,
        p_quantity_received: input.quantityReceived,
        p_cost_per_unit: input.costPerUnit,
        p_receipt_date: input.receiptDate.toISOString(),
        p_supplier: input.supplier,
        p_invoice_number: input.invoiceNumber,
        p_notes: input.notes,
      });

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['receipts', restaurantId] });
      queryClient.invalidateQueries({ queryKey: queryKeys.inventory.all() });
      queryClient.invalidateQueries({ queryKey: queryKeys.inventory.list({ restaurantId }) });
      queryClient.invalidateQueries({ queryKey: ['cost-analytics', restaurantId] });
      queryClient.invalidateQueries({ queryKey: ['inventory-insights'] });
      queryClient.invalidateQueries({ queryKey: ['menuItemAvailability', restaurantId] });
      queryClient.invalidateQueries({ queryKey: ['menuItemOptionAvailability'] });

      toast.success('Receipt updated successfully');
    },
    onError: (error: Error) => {
      toast.error('Failed to update receipt: ' + error.message);
    },
  });
}

// Input for deleting a receipt
export interface DeleteReceiptInput {
  receiptId: string;
}

/**
 * Hook to delete a receipt with inventory reversal
 */
export function useDeleteReceipt(restaurantId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: DeleteReceiptInput) => {
      if (isAssistantLoggedIn()) {

        const { data, error } = await supabase.rpc('assistant_delete_inventory_receipt' as any, {
          
          p_receipt_id: input.receiptId,
          p_restaurant_id: restaurantId,
        });

        if (error) {
          if (error.message?.includes('PERMISSION_DENIED')) {
            throw new Error('Permission denied: You do not have permission to delete receipts');
          }
          throw error;
        }

        return data;
      }

      // Admin user
      const { data, error } = await supabase.rpc('delete_inventory_receipt' as any, {
        p_receipt_id: input.receiptId,
        p_restaurant_id: restaurantId,
      });

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['receipts', restaurantId] });
      queryClient.invalidateQueries({ queryKey: queryKeys.inventory.all() });
      queryClient.invalidateQueries({ queryKey: queryKeys.inventory.list({ restaurantId }) });
      queryClient.invalidateQueries({ queryKey: ['cost-analytics', restaurantId] });
      queryClient.invalidateQueries({ queryKey: ['inventory-insights'] });
      queryClient.invalidateQueries({ queryKey: ['menuItemAvailability', restaurantId] });
      queryClient.invalidateQueries({ queryKey: ['menuItemOptionAvailability'] });

      toast.success('Receipt deleted successfully');
    },
    onError: (error: Error) => {
      toast.error('Failed to delete receipt: ' + error.message);
    },
  });
}

/**
 * Hook to delete multiple receipts (for grouped deletion)
 */
export function useDeleteMultipleReceipts(restaurantId: string) {
  const deleteReceiptMutation = useDeleteReceipt(restaurantId);

  return useMutation({
    mutationFn: async (receiptIds: string[]) => {
      const results: Array<{ receiptId: string; success: boolean; error?: string }> = [];

      // Delete receipts sequentially to maintain order and error tracking
      for (const receiptId of receiptIds) {
        try {
          await deleteReceiptMutation.mutateAsync({ receiptId });
          results.push({ receiptId, success: true });
        } catch (error) {
          results.push({
            receiptId,
            success: false,
            error: error instanceof Error ? error.message : 'Unknown error',
          });
        }
      }

      const successCount = results.filter((r) => r.success).length;
      const failureCount = results.filter((r) => !r.success).length;

      return {
        results,
        successCount,
        failureCount,
        totalCount: receiptIds.length,
      };
    },
    onSuccess: (result) => {
      // Queries are already invalidated by individual delete mutations
      if (result.failureCount === 0) {
        toast.success(`All ${result.totalCount} receipts deleted successfully`);
      } else if (result.successCount > 0) {
        toast.warning(
          `Deleted ${result.successCount} of ${result.totalCount} receipts. ${result.failureCount} failed.`
        );
      } else {
        toast.error('Failed to delete all receipts');
      }
    },
    onError: (error: Error) => {
      toast.error('Failed to delete receipts: ' + error.message);
    },
  });
}
